import { BOARD_THEMES, type BoardColors } from "@/constants/boardColors";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

interface BoardSettings {
  // Theme settings
  darkMode: boolean;
  boardTheme: BoardColors;
  // Board appearance
  showCoordinates: boolean;
  showLegalMoves: boolean;
  showSquareNotation: boolean;
  // Piece settings
  // Animation settings
  enableAnimations: boolean;
  animationSpeed: "slow" | "normal" | "fast";
  emojiFont: "apple" | "google" | "windows" | "twitter" | "open";
  // Dialog settings
  showFirstTimeDialog: boolean;
}

interface BoardSettingsStore extends BoardSettings {
  // Actions
  setDarkMode: (darkMode: boolean) => void;
  setBoardTheme: (theme: BoardColors) => void;
  setShowCoordinates: (show: boolean) => void;
  setShowLegalMoves: (show: boolean) => void;
  setShowSquareNotation: (show: boolean) => void;
  setEnableAnimations: (enable: boolean) => void;
  setAnimationSpeed: (speed: "slow" | "normal" | "fast") => void;
  setEmojiFont: (
    font: "apple" | "google" | "windows" | "twitter" | "open"
  ) => void;
  setShowFirstTimeDialog: (show: boolean) => void;
  resetSettings: () => void;
}

const initialState: BoardSettings = {
  darkMode: true,
  boardTheme: BOARD_THEMES[2], // Default to Classic theme
  showCoordinates: true,
  showLegalMoves: true,
  showSquareNotation: true,
  enableAnimations: true,
  animationSpeed: "normal",
  emojiFont: "google",
  showFirstTimeDialog: false,
};

export const useBoardSettingsStore = create<BoardSettingsStore>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setDarkMode: (darkMode) => set({ darkMode }),
        setBoardTheme: (boardTheme) => set({ boardTheme }),
        setShowCoordinates: (showCoordinates) => set({ showCoordinates }),
        setShowLegalMoves: (showLegalMoves) => set({ showLegalMoves }),
        setShowSquareNotation: (showSquareNotation) =>
          set({ showSquareNotation }),
        setEnableAnimations: (enableAnimations) => set({ enableAnimations }),
        setAnimationSpeed: (animationSpeed) => set({ animationSpeed }),
        setEmojiFont: (emojiFont) => set({ emojiFont }),
        setShowFirstTimeDialog: (showFirstTimeDialog) =>
          set({ showFirstTimeDialog }),
        resetSettings: () => set(initialState),
      }),
      {
        name: "board-settings-storage",
        // Only persist these fields
        partialize: (state) => ({
          darkMode: state.darkMode,
          boardTheme: state.boardTheme,
          animationSpeed: state.animationSpeed,
          emojiFont: state.emojiFont,
          showSquareNotation: state.showSquareNotation,
          showFirstTimeDialog: state.showFirstTimeDialog,
        }),
      }
    )
  )
);

